const AUTH_API_BASE_URL = (process.env.REACT_APP_AUTH_API_BASE_URL || '').replace(/\/$/, '');
const AUTH_API_PATH = '/api/auth';
const SESSION_TOKEN_STORAGE_KEY = 'webtika.auth.token';
const SESSION_USER_STORAGE_KEY = 'webtika.auth.user';
const ADMIN_BACKUP_TOKEN_STORAGE_KEY = 'webtika.auth.admin-backup.token';
const ADMIN_BACKUP_USER_STORAGE_KEY = 'webtika.auth.admin-backup.user';
const SESSION_REFRESH_REQUIRED_ERRORS = new Set(['stale_session', 'session_refresh_required']);
const SESSION_EXPIRED_ERRORS = new Set(['session_expired', 'auth_required']);
const DOCKER_INTERNAL_AUTH_HOSTS = new Set(['auth-service']);
const LOCAL_ONLY_AUTH_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);
const CONTROL_PLANE_DEBUG_ENVIRONMENTS = new Set(['development', 'dev', 'prealpha', 'test']);

function isLocalBrowserHostname(hostname) {
  return LOCAL_ONLY_AUTH_HOSTS.has((hostname || '').trim().toLowerCase());
}

export function getResolvedAuthApiBaseUrl() {
  if (!AUTH_API_BASE_URL) {
    return '';
  }

  if (typeof window === 'undefined') {
    return AUTH_API_BASE_URL;
  }

  try {
    const parsedUrl = new URL(AUTH_API_BASE_URL, window.location.origin);
    const configuredHostname = (parsedUrl.hostname || '').trim().toLowerCase();
    const browserHostname = (window.location.hostname || '').trim().toLowerCase();

    if (DOCKER_INTERNAL_AUTH_HOSTS.has(configuredHostname)) {
      return '';
    }

    if (LOCAL_ONLY_AUTH_HOSTS.has(configuredHostname) && !isLocalBrowserHostname(browserHostname)) {
      return '';
    }

    return AUTH_API_BASE_URL;
  } catch {
    return AUTH_API_BASE_URL;
  }
}

export function getAuthApiUrl(path = AUTH_API_PATH) {
  return `${getResolvedAuthApiBaseUrl()}${path}`;
}

export function getRuntimeEnvironment() {
  return String(
    process.env.REACT_APP_RUNTIME_ENVIRONMENT || process.env.NODE_ENV || 'development'
  ).trim().toLowerCase();
}

export function shouldDebugControlPlaneSession() {
  return CONTROL_PLANE_DEBUG_ENVIRONMENTS.has(getRuntimeEnvironment());
}

function canUseSessionStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function dispatchSessionUserChanged() {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  window.dispatchEvent(new CustomEvent('webtika:session-user-changed'));
}

function parseErrorMessage(payload, fallbackMessage) {
  if (payload && typeof payload === 'object' && typeof payload.message === 'string') {
    return payload.message;
  }

  if (payload && typeof payload === 'object' && typeof payload.error === 'string') {
    return payload.error;
  }

  return fallbackMessage;
}

function buildSessionError(payload, fallbackMessage) {
  const error = new Error(parseErrorMessage(payload, fallbackMessage));
  error.code = payload && typeof payload.error === 'string' ? payload.error : '';
  return error;
}

export function isSessionRefreshRequiredPayload(payload) {
  return Boolean(payload && SESSION_REFRESH_REQUIRED_ERRORS.has(payload.error));
}

export function isSessionRefreshRequiredError(error) {
  return Boolean(error && SESSION_REFRESH_REQUIRED_ERRORS.has(error.code));
}

export function isSessionExpiredPayload(payload) {
  return Boolean(payload && SESSION_EXPIRED_ERRORS.has(payload.error));
}

export function isSessionExpiredError(error) {
  return Boolean(error && SESSION_EXPIRED_ERRORS.has(error.code));
}

async function parseJson(response) {
  let responseToParse = response;

  try {
    // Parse a cloned response so downstream consumers can still inspect the original body.
    responseToParse = response.clone();
  } catch {
    responseToParse = response;
  }

  const text = await responseToParse.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function normalizeSessionUser(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const tenantPayload = payload.tenant && typeof payload.tenant === 'object' ? payload.tenant : null;
  const normalizedTenantId = payload.tenantId || tenantPayload?.id || '';
  const rawCapabilities =
    payload.capabilities && typeof payload.capabilities === 'object' ? payload.capabilities : null;
  const normalizedCapabilities = rawCapabilities
    ? {
        tenantId: rawCapabilities.tenantId || normalizedTenantId,
        tenantActive: rawCapabilities.tenantActive === true,
        membershipStatus:
          typeof rawCapabilities.membershipStatus === 'string'
            ? rawCapabilities.membershipStatus
            : '',
        enabledModuleKeys: Array.isArray(rawCapabilities.enabledModuleKeys)
          ? rawCapabilities.enabledModuleKeys
          : [],
        canAccessPlatformAdmin: rawCapabilities.canAccessPlatformAdmin === true,
        canEnterWorkspaceAccess: rawCapabilities.canEnterWorkspaceAccess === true,
        canAccessWorkspace: rawCapabilities.canAccessWorkspace === true,
        canCreateStaff: rawCapabilities.canCreateStaff === true,
        canManageStaffDestructiveActions:
          rawCapabilities.canManageStaffDestructiveActions === true,
        canManageDocumentDestructiveActions:
          rawCapabilities.canManageDocumentDestructiveActions === true,
        canManageDealDestructiveActions:
          rawCapabilities.canManageDealDestructiveActions === true,
        canManageAccountingDestructiveActions:
          rawCapabilities.canManageAccountingDestructiveActions === true,
        canManageWorkspaceUsers: rawCapabilities.canManageWorkspaceUsers === true,
        canManageWorkspaceUserRoles:
          rawCapabilities.canManageWorkspaceUserRoles === true,
        canManageWorkspaceSettings: rawCapabilities.canManageWorkspaceSettings === true,
      }
    : null;

  return {
    id: payload.id ?? null,
    tenantId: normalizedTenantId,
    tenant: tenantPayload
      ? {
          id: tenantPayload.id || normalizedTenantId,
          name: tenantPayload.name || '',
          active: tenantPayload.active !== false,
          slug: tenantPayload.slug || '',
          planCode: tenantPayload.planCode || '',
          enabledModuleKeys: Array.isArray(tenantPayload.enabledModuleKeys)
            ? tenantPayload.enabledModuleKeys
            : [],
          settings:
            tenantPayload.settings && typeof tenantPayload.settings === 'object'
              ? tenantPayload.settings
              : {},
        }
      : normalizedTenantId
        ? {
            id: normalizedTenantId,
            name: '',
            active: true,
            slug: '',
            planCode: '',
            enabledModuleKeys: [],
            settings: {},
          }
        : null,
    name: payload.name || '',
    email: payload.email || '',
    role: payload.role || '',
    workspaceAccess: payload.workspaceAccess === true,
    mustChangePassword: payload.mustChangePassword === true,
    capabilities: normalizedCapabilities,
  };
}

function decodeBase64UrlSegment(segment) {
  if (!segment || typeof segment !== 'string') {
    return null;
  }

  try {
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
    const encoded = `${normalized}${padding}`;

    if (typeof window !== 'undefined' && typeof window.atob === 'function') {
      return window.atob(encoded);
    }

    return Buffer.from(encoded, 'base64').toString('utf-8');
  } catch {
    return null;
  }
}

export function readJwtClaims(token) {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const tokenParts = token.split('.');
  if (tokenParts.length < 2) {
    return null;
  }

  const payloadJson = decodeBase64UrlSegment(tokenParts[1]);
  if (!payloadJson) {
    return null;
  }

  try {
    const payload = JSON.parse(payloadJson);
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

export function getSessionTokenExpirationMs(token) {
  const claims = readJwtClaims(token);
  const expirationSeconds = claims?.exp;

  if (typeof expirationSeconds !== 'number' || !Number.isFinite(expirationSeconds)) {
    return null;
  }

  return expirationSeconds * 1000;
}

export function getStoredSessionToken() {
  if (!canUseSessionStorage()) {
    return '';
  }

  return window.localStorage.getItem(SESSION_TOKEN_STORAGE_KEY) || '';
}

export function storeSessionToken(token) {
  if (!canUseSessionStorage()) {
    return;
  }

  if (!token) {
    window.localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(SESSION_TOKEN_STORAGE_KEY, token);
}

export function getStoredSessionUser() {
  if (!canUseSessionStorage()) {
    return null;
  }

  const rawValue = window.localStorage.getItem(SESSION_USER_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    return normalizeSessionUser(JSON.parse(rawValue));
  } catch {
    return null;
  }
}

export function storeSessionUser(user) {
  if (!canUseSessionStorage()) {
    return;
  }

  if (!user) {
    window.localStorage.removeItem(SESSION_USER_STORAGE_KEY);
    dispatchSessionUserChanged();
    return;
  }

  window.localStorage.setItem(SESSION_USER_STORAGE_KEY, JSON.stringify(user));
  dispatchSessionUserChanged();
}

export function clearStoredSessionToken() {
  if (!canUseSessionStorage()) {
    return;
  }

  window.localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
}

export function clearStoredSessionUser() {
  if (!canUseSessionStorage()) {
    return;
  }

  window.localStorage.removeItem(SESSION_USER_STORAGE_KEY);
  dispatchSessionUserChanged();
}

export function clearStoredAdminBackupSession() {
  if (!canUseSessionStorage()) {
    return;
  }

  window.localStorage.removeItem(ADMIN_BACKUP_TOKEN_STORAGE_KEY);
  window.localStorage.removeItem(ADMIN_BACKUP_USER_STORAGE_KEY);
}

export function storeActiveSession({ token, user }) {
  storeSessionToken(token);
  storeSessionUser(user);
}

export async function loginSession({ email, password }) {
  const response = await fetch(getAuthApiUrl(`${AUTH_API_PATH}/login`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  const payload = await parseJson(response);

  if (!response.ok) {
    throw buildSessionError(payload, 'Login non riuscito');
  }

  const token = payload?.token || '';
  const user = normalizeSessionUser(payload?.user);

  if (!token || !user) {
    throw new Error('Risposta login non valida');
  }

  storeSessionToken(token);
  storeSessionUser(user);

  return {
    token,
    expiresAt: payload?.expiresAt || null,
    user,
  };
}

export async function fetchCurrentSessionUser(token = getStoredSessionToken()) {
  if (!token) {
    return null;
  }

  const response = await fetch(getAuthApiUrl(`${AUTH_API_PATH}/me`), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = await parseJson(response);

  if (!response.ok) {
    throw buildSessionError(payload, 'Sessione non valida');
  }

  const user = normalizeSessionUser(payload);
  storeSessionUser(user);
  return user;
}

export async function refreshSession(token = getStoredSessionToken()) {
  if (!token) {
    const error = new Error('Sessione scaduta. Effettua di nuovo il login.');
    error.code = 'auth_required';
    throw error;
  }

  const response = await fetch(getAuthApiUrl(`${AUTH_API_PATH}/session/refresh`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = await parseJson(response);

  if (!response.ok) {
    throw buildSessionError(payload, 'Sessione non valida');
  }

  const nextToken = payload?.token || '';
  const user = normalizeSessionUser(payload?.user);

  if (!nextToken || !user) {
    throw new Error('Risposta refresh sessione non valida');
  }

  storeSessionToken(nextToken);
  storeSessionUser(user);

  return {
    token: nextToken,
    expiresAt: payload?.expiresAt || null,
    user,
  };
}

export async function changeOwnPassword({ currentPassword, newPassword }) {
  const token = getStoredSessionToken();

  if (!token) {
    throw new Error('Sessione non disponibile');
  }

  const response = await fetch(getAuthApiUrl(`${AUTH_API_PATH}/password`), {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  const payload = await parseJson(response);

  if (!response.ok) {
    throw buildSessionError(payload, 'Cambio password non riuscito');
  }

  const user = normalizeSessionUser(payload);
  if (!user) {
    throw new Error('Risposta cambio password non valida');
  }

  storeSessionUser(user);
  return user;
}

export async function updateCurrentUserEmail(email) {
  const token = getStoredSessionToken();

  if (!token) {
    throw new Error('Sessione non disponibile');
  }

  const response = await fetch(getAuthApiUrl(`${AUTH_API_PATH}/me/email`), {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const payload = await parseJson(response);

  if (!response.ok) {
    throw buildSessionError(payload, 'Cambio email non riuscito');
  }

  const nextToken = payload?.token || '';
  const user = normalizeSessionUser(payload?.user);

  if (!nextToken || !user) {
    throw new Error('Risposta cambio email non valida');
  }

  storeSessionToken(nextToken);
  storeSessionUser(user);

  return {
    token: nextToken,
    expiresAt: payload?.expiresAt || null,
    user,
  };
}
